/**
 * Box detail page — shows box info, items list, edit capabilities.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBoxStore } from '../store/BoxContext';
import { ItemList } from '../components/ItemList';
import { EditModal } from '../components/EditModal';
import { EmptyState } from '../components/EmptyState';
import { BoxDetailSkeleton } from '../components/Skeleton';
import { PhotoGallery } from '../components/PhotoGallery';
import { useToast } from '../store/ToastContext';
import { uploadBoxPhoto, deleteBoxPhoto } from '../api/boxPhotos';

export function BoxDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    state,
    fetchBox,
    clearCurrentBox,
    updateBox,
    removeBox,
    addItem,
    updateItem,
    adjustItemQuantity,
    removeItem,
  } = useBoxStore();
  const { addToast } = useToast();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const box = state.currentBox;

  useEffect(() => {
    if (id) fetchBox(id);
    return () => clearCurrentBox();
  }, [id, fetchBox, clearCurrentBox]);

  // Pre-fill edit form when box loads
  useEffect(() => {
    if (box) {
      setEditName(box.name);
      setEditLocation(box.location ?? '');
      setEditNotes(box.notes ?? '');
    }
  }, [box]);

  async function handleSaveEdit() {
    if (!box) return;
    setSaving(true);
    try {
      await updateBox(box.id, {
        name: editName || undefined,
        location: editLocation || null,
        notes: editNotes || null,
      });
      setEditModalOpen(false);
      addToast('success', 'Box updated');
    } catch {
      addToast('error', 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!box) return;
    try {
      await removeBox(box.id);
      addToast('success', 'Box deleted');
      navigate('/boxes');
    } catch {
      addToast('error', 'Failed to delete box');
    }
  }

  async function handleGalleryUpload(file: File, caption?: string) {
    if (!box) return;
    setPhotoUploading(true);
    try {
      await uploadBoxPhoto(box.id, file, caption);
      if (id) await fetchBox(id);
      addToast('success', 'Photo uploaded');
    } catch {
      addToast('error', 'Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  }

  async function handleGalleryDelete(photoId: string) {
    if (!box) return;
    try {
      await deleteBoxPhoto(photoId);
      if (id) await fetchBox(id);
      addToast('success', 'Photo removed');
    } catch {
      addToast('error', 'Failed to remove photo');
    }
  }

  async function handleAddItem(name: string) {
    if (!box) return;
    try {
      await addItem(box.id, { name });
      addToast('success', 'Item added');
    } catch {
      addToast('error', 'Failed to add item');
    }
  }

  async function handleUpdateItem(itemId: string, data: { name?: string; quantity?: number }) {
    try {
      await updateItem(itemId, data);
    } catch {
      addToast('error', 'Failed to update item');
    }
  }

  async function handleAdjustQuantity(itemId: string, delta: number) {
    try {
      await adjustItemQuantity(itemId, delta);
    } catch {
      addToast('error', 'Failed to adjust quantity');
    }
  }

  async function handleDeleteItem(itemId: string) {
    try {
      await removeItem(itemId);
    } catch {
      addToast('error', 'Failed to delete item');
    }
  }

  if (state.currentBoxLoading) {
    return <BoxDetailSkeleton />;
  }

  if (state.currentBoxError || !box) {
    return (
      <EmptyState
        title="Box not found"
        description={state.currentBoxError ?? 'This box may have been deleted.'}
        action={{ label: 'Back to boxes', onClick: () => navigate('/boxes') }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/boxes')}
        className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to boxes
      </button>

      {/* Box header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{box.name}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-text-secondary">
            <span className="rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium text-accent uppercase">
              {box.markerType === 'aruco' ? `ArUco #${box.markerId}` : `QR: ${box.markerId}`}
            </span>
            {box.location && (
              <span className="flex items-center gap-1">
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {box.location}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setEditModalOpen(true)}
          className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary hover:bg-surface transition-colors"
          aria-label="Edit box"
        >
          Edit
        </button>
      </div>

      {/* Notes */}
      {box.notes && (
        <div className="rounded-lg border border-border bg-surface p-3">
          <p className="text-sm text-text-secondary">{box.notes}</p>
        </div>
      )}

      {/* Photos */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Photos {box.photos && box.photos.length > 0 && `(${box.photos.length})`}
        </h2>
        <PhotoGallery
          photos={box.photos || []}
          onUpload={handleGalleryUpload}
          onDelete={handleGalleryDelete}
          uploading={photoUploading}
        />
      </div>

      {/* Items */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Items ({box.items.length})
        </h2>
        <ItemList
          items={box.items}
          onAdd={handleAddItem}
          onUpdate={handleUpdateItem}
          onAdjustQuantity={handleAdjustQuantity}
          onDelete={handleDeleteItem}
        />
      </div>

      {/* Delete zone */}
      <div className="border-t border-border pt-6">
        {deleteConfirm ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-danger">Delete this box and all its items?</p>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-lg bg-danger px-3 py-1.5 text-sm font-semibold text-white hover:bg-danger/80 transition-colors"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirm(false)}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary hover:bg-surface transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setDeleteConfirm(true)}
            className="text-sm text-danger hover:text-danger/80 transition-colors"
          >
            Delete box
          </button>
        )}
      </div>

      {/* Edit Modal */}
      <EditModal open={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit Box">
        <div className="space-y-4">
          <div>
            <label htmlFor="edit-name" className="block text-sm font-medium text-text-secondary">
              Name
            </label>
            <input
              id="edit-name"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div>
            <label
              htmlFor="edit-location"
              className="block text-sm font-medium text-text-secondary"
            >
              Location
            </label>
            <input
              id="edit-location"
              type="text"
              value={editLocation}
              onChange={(e) => setEditLocation(e.target.value)}
              placeholder="e.g. Garage, Basement"
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div>
            <label htmlFor="edit-notes" className="block text-sm font-medium text-text-secondary">
              Notes
            </label>
            <textarea
              id="edit-notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setEditModalOpen(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary hover:bg-surface transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={saving || !editName.trim()}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </EditModal>
    </div>
  );
}
